/**
 * Affiliate account content — layout-agnostic.
 *
 * Mounted in two places:
 *  - /pass/affiliate          → standalone page (AffiliateDashboard wraps it)
 *  - /mi-cuenta tab "Afiliados" → inside CustomerDashboard
 *
 * Auth: customer_token via passApi.passCustomerHttp.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import QRCode from 'qrcode';
import {
  QrCode, Download, Loader2, FileText, Share2, Wallet, CheckCircle2, Clock,
  Sparkles, Eye, EyeOff, Star, Image as ImageIcon, BookOpen, User, Upload, Save, IdCard,
} from 'lucide-react';
import {
  passAffiliates, passWallet, getCustomerToken,
} from '../../services/passApi';

export default function AffiliateAccountContent() {
  const customerToken = getCustomerToken();
  const [loading, setLoading] = useState(true);
  const [affiliate, setAffiliate] = useState(null);
  const [showRegister, setShowRegister] = useState(false);
  const [commissions, setCommissions] = useState({ commissions: [], summary: {} });
  const [wallet, setWallet] = useState({
    balance: { available: 0, earned_total: 0, paid_total: 0, requested_in_progress: 0 },
    payouts: [],
  });
  const [registering, setRegistering] = useState(false);
  const [requesting, setRequesting] = useState(false);

  const [form, setForm] = useState({ display_name: '', custom_slug: '', custom_affiliate_code: '', phone: '' });

  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutMethod, setPayoutMethod] = useState('SPEI');
  const [payoutDest, setPayoutDest] = useState('');

  useEffect(() => {
    if (!customerToken) {
      setLoading(false);
      return;
    }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerToken]);

  async function loadAll() {
    setLoading(true);
    try {
      const res = await passAffiliates.me();
      setAffiliate(res.data.affiliate);
      try {
        const c = await passAffiliates.commissions();
        setCommissions(c.data);
      } catch (_) {}
      try {
        const w = await passWallet.me();
        setWallet(w.data);
      } catch (_) {}
    } catch (err) {
      if (err.response?.status === 404) {
        setShowRegister(true);
      } else {
        toast.error(err.response?.data?.detail || 'Error cargando perfil');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleRegister(e) {
    e.preventDefault();
    setRegistering(true);
    try {
      const res = await passAffiliates.register({
        display_name: form.display_name,
        custom_slug: form.custom_slug || null,
        custom_affiliate_code: form.custom_affiliate_code || null,
        phone: form.phone || null,
      });
      setAffiliate(res.data.affiliate);
      setShowRegister(false);
      toast.success('Bienvenido a Pass — tu QR está listo');
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo registrar');
    } finally {
      setRegistering(false);
    }
  }

  if (!customerToken) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center">
        <Sparkles className="w-8 h-8 mx-auto text-teal-500 mb-2" />
        <h2 className="text-lg font-bold text-slate-900">Activa tu modo afiliado</h2>
        <p className="text-sm text-slate-500 mt-1 mb-4">
          Para crear tu QR y ganar comisiones necesitas tu cuenta de Todo en Local.
        </p>
        <Link
          to="/auth?next=/pass/affiliate"
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm"
        >
          Iniciar sesión / Crear cuenta
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
    );
  }

  if (showRegister) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <h2 className="text-xl font-bold text-slate-900 mb-1 inline-flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-teal-600" /> Conviértete en afiliado Pass
        </h2>
        <p className="text-sm text-slate-500 mb-5">
          Crea tu perfil en menos de un minuto. Te asignamos un QR y código únicos.
        </p>
        <form onSubmit={handleRegister} className="space-y-4">
          <input
            required
            className="w-full border border-slate-300 rounded-lg px-3 py-2"
            placeholder="Tu nombre o alias (ej. Carlos Taxi 77)"
            value={form.display_name}
            onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
          />
          <input
            className="w-full border border-slate-300 rounded-lg px-3 py-2"
            placeholder="Slug personalizado: aparece en tu URL (opcional)"
            value={form.custom_slug}
            onChange={(e) => setForm((f) => ({ ...f, custom_slug: e.target.value }))}
          />
          <input
            className="w-full border border-slate-300 rounded-lg px-3 py-2 font-mono uppercase"
            placeholder="Código personalizado: CARLOS77 (opcional, mín 4 chars)"
            maxLength={20}
            value={form.custom_affiliate_code}
            onChange={(e) => setForm((f) => ({ ...f, custom_affiliate_code: e.target.value.toUpperCase() }))}
          />
          <input
            className="w-full border border-slate-300 rounded-lg px-3 py-2"
            placeholder="WhatsApp (opcional)"
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
          />
          <button
            type="submit"
            disabled={registering}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-semibold disabled:opacity-60"
          >
            {registering && <Loader2 className="w-4 h-4 animate-spin" />} Crear mi perfil
          </button>
        </form>
      </div>
    );
  }

  if (!affiliate) return null;
  const publicUrl = `${window.location.origin}/pass/${affiliate.slug}`;
  const waShare = encodeURIComponent(`Mira las promos que recomiendo en Vallarta: ${publicUrl}`);

  async function handlePayoutRequest(e) {
    e.preventDefault();
    const amount = Number(payoutAmount);
    if (!amount || amount <= 0) return;
    setRequesting(true);
    try {
      await passWallet.requestPayout({
        amount,
        method: payoutMethod,
        destination: { account: payoutDest || null },
      });
      toast.success('Solicitud enviada — esperando aprobación');
      setPayoutAmount('');
      setPayoutDest('');
      const w = await passWallet.me();
      setWallet(w.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo solicitar');
    } finally {
      setRequesting(false);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-4">
        {affiliate.avatar_url ? (
          <img src={affiliate.avatar_url} alt={affiliate.display_name} className="w-16 h-16 rounded-full object-cover border-2 border-teal-500" />
        ) : (
          <div className="w-16 h-16 rounded-full bg-slate-100 border-2 border-slate-200 flex items-center justify-center text-slate-400">
            <User className="w-7 h-7" />
          </div>
        )}
        <div>
          <h1 className="text-xl font-bold text-slate-900">{affiliate.display_name}</h1>
          <p className="text-sm text-slate-500">Código: <span className="font-mono">{affiliate.affiliate_code}</span></p>
        </div>
      </header>

      {/* Presentation editor */}
      <PresentationEditor affiliate={affiliate} onChange={(next) => setAffiliate(next)} />

      {/* My QR */}
      <Section title="Mi QR" icon={QrCode}>
        <div className="flex flex-col md:flex-row gap-6 items-center">
          <QrPreview qrToken={customerToken} affiliate={affiliate} />
          <div className="flex-1 w-full space-y-2 text-sm">
            <Row label="URL pública" value={<a className="text-teal-600 underline break-all" href={publicUrl}>{publicUrl}</a>} />
            <Row label="Slug" value={affiliate.slug} />
            <a
              href={`https://wa.me/?text=${waShare}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500 hover:bg-green-600 text-white font-semibold"
            >
              <Share2 className="w-4 h-4" /> Compartir por WhatsApp
            </a>
          </div>
        </div>
      </Section>

      {/* Print Material */}
      <Section title="Material para imprimir" icon={FileText} accent="orange">
        <p className="text-sm text-slate-500 mb-3">
          Descarga, imprime y enmica. Pega el PDF detrás del asiento, en el mostrador o en recepción.
        </p>
        <div className="grid sm:grid-cols-3 gap-3">
          <DownloadBtn label="Carta — Taxi" size="letter" template="taxi_01" />
          <DownloadBtn label="Media carta — Taxi" size="half_letter" template="taxi_01" />
          <DownloadBtn label="Carta — Concierge" size="letter" template="concierge_01" />
        </div>
        <div className="mt-5 pt-4 border-t border-slate-200">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 inline-flex items-center gap-1">
            <IdCard className="w-3.5 h-3.5" /> Tarjeta de presentación (3.5 × 2 in)
          </p>
          <p className="text-sm text-slate-500 mb-3">
            Imprime tarjetas para entregar en mano a tus clientes. Incluye tu foto, nombre, código y QR.
          </p>
          <DownloadBtn label="Tarjeta de presentación" size="business_card" template="taxi_01" />
        </div>
      </Section>

      {/* Curated Catalog */}
      <CuratedCatalog />

      {/* Curated Guides */}
      <CuratedGuides />

      {/* Wallet */}
      <Section title="Wallet" icon={Wallet}>
        <div className="grid sm:grid-cols-4 gap-3 mb-5">
          <Stat label="Disponible" value={wallet.balance.available} accent="orange" />
          <Stat label="Ganado total" value={wallet.balance.earned_total} />
          <Stat label="En proceso" value={wallet.balance.requested_in_progress} />
          <Stat label="Pagado" value={wallet.balance.paid_total} />
        </div>
        <form onSubmit={handlePayoutRequest} className="grid sm:grid-cols-4 gap-2 mb-5">
          <input
            type="number" step="0.01" min="0.01" max={wallet.balance.available}
            placeholder={`Monto (máx $${wallet.balance.available.toFixed(2)})`}
            value={payoutAmount}
            onChange={(e) => setPayoutAmount(e.target.value)}
            className="sm:col-span-1 border border-slate-300 rounded-lg px-3 py-2 text-sm"
          />
          <select value={payoutMethod} onChange={(e) => setPayoutMethod(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
            <option value="SPEI">SPEI</option>
            <option value="cash">Efectivo</option>
            <option value="other">Otro</option>
          </select>
          <input
            type="text" placeholder="Cuenta / referencia"
            value={payoutDest}
            onChange={(e) => setPayoutDest(e.target.value)}
            className="sm:col-span-1 border border-slate-300 rounded-lg px-3 py-2 text-sm"
          />
          <button
            type="submit" disabled={requesting || wallet.balance.available <= 0}
            className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold disabled:opacity-60"
          >
            {requesting && <Loader2 className="w-4 h-4 animate-spin" />} Solicitar retiro
          </button>
        </form>

        {wallet.payouts.length > 0 && (
          <div>
            <h3 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Historial de retiros</h3>
            <div className="divide-y divide-slate-200">
              {wallet.payouts.map((p) => (
                <div key={p.id} className="py-2 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-semibold text-slate-900">${Number(p.amount).toFixed(2)}</div>
                    <div className="text-xs text-slate-500">{p.method || '—'} · {new Date(p.created_at).toLocaleString()}</div>
                  </div>
                  <PayoutStatusBadge status={p.status} />
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-xs text-slate-500 mt-4">
          Comisiones registradas: {commissions.commissions?.length || 0}
        </p>
      </Section>
    </div>
  );
}

/* ----------- Subcomponents ----------- */

function PresentationEditor({ affiliate, onChange }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    display_name: affiliate.display_name || '',
    bio: affiliate.bio || '',
    affiliate_code: affiliate.affiliate_code || '',
  });

  useEffect(() => {
    setForm({
      display_name: affiliate.display_name || '',
      bio: affiliate.bio || '',
      affiliate_code: affiliate.affiliate_code || '',
    });
  }, [affiliate.display_name, affiliate.bio, affiliate.affiliate_code]);

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await passAffiliates.uploadAvatar(file);
      onChange({ ...affiliate, avatar_url: res.data.avatar_url });
      toast.success('Foto actualizada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error subiendo foto');
    } finally {
      setUploading(false);
    }
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        display_name: form.display_name || null,
        bio: form.bio || null,
      };
      if (form.affiliate_code && form.affiliate_code !== affiliate.affiliate_code) {
        payload.affiliate_code = form.affiliate_code;
      }
      const res = await passAffiliates.updateMe(payload);
      onChange(res.data.affiliate);
      setEditing(false);
      toast.success('Presentación guardada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Section title="Mi presentación" icon={User}>
      <p className="text-sm text-slate-500 mb-4">
        Tu foto, nombre y mensaje aparecen en tu perfil público y en el material impreso. Una buena presentación cierra ventas.
      </p>

      <div className="flex flex-col sm:flex-row gap-5 items-start">
        {/* Avatar */}
        <div className="flex flex-col items-center">
          {affiliate.avatar_url ? (
            <img
              src={affiliate.avatar_url}
              alt={affiliate.display_name}
              className="w-24 h-24 rounded-full object-cover border-2 border-teal-500"
            />
          ) : (
            <div className="w-24 h-24 rounded-full bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400">
              <User className="w-10 h-10" />
            </div>
          )}
          <label className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-xs font-semibold text-slate-700 cursor-pointer">
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
            {uploading ? 'Subiendo...' : (affiliate.avatar_url ? 'Cambiar foto' : 'Subir foto')}
            <input type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
          </label>
        </div>

        {/* Editable fields */}
        <div className="flex-1 w-full">
          {!editing ? (
            <div>
              <p className="text-base font-semibold text-slate-900">{affiliate.display_name}</p>
              {affiliate.bio ? (
                <p className="text-sm text-slate-600 italic mt-1">"{affiliate.bio}"</p>
              ) : (
                <p className="text-sm text-slate-400 italic mt-1">
                  Sin mensaje de presentación. Cuenta a tus clientes quién eres en pocas palabras.
                </p>
              )}
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="mt-3 inline-flex items-center gap-1 text-sm text-teal-600 hover:underline font-semibold"
              >
                {affiliate.bio ? 'Editar presentación' : 'Escribir presentación'}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSave} className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-700">Tu nombre / alias</label>
                <input
                  required maxLength={80}
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  value={form.display_name}
                  onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">Código de afiliado <span className="text-slate-400 font-normal">(letras/dígitos, mín 4)</span></label>
                <input
                  maxLength={20}
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono uppercase"
                  value={form.affiliate_code}
                  onChange={(e) => setForm((f) => ({ ...f, affiliate_code: e.target.value.toUpperCase() }))}
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Aparece en tu material impreso y se usa para tracking de comisiones. Cámbialo si quieres uno más corto o memorable.
                </p>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">Mensaje a tus clientes <span className="text-slate-400 font-normal">({form.bio.length}/280)</span></label>
                <textarea
                  maxLength={280} rows={3}
                  placeholder="Ej. 25 años llevando turistas en Vallarta. Te recomiendo solo lo mejor — limpio, seguro y siempre con buena onda."
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  value={form.bio}
                  onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Aparece en tu perfil público y en el material impreso. Sé breve y honesto — la cercanía vende.
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  type="submit" disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold disabled:opacity-60"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Guardar
                </button>
                <button
                  type="button" onClick={() => setEditing(false)}
                  className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm"
                >
                  Cancelar
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </Section>
  );
}

function QrPreview({ qrToken, affiliate }) {
  const [imgUrl, setImgUrl] = useState(null);
  const [fallbackOk, setFallbackOk] = useState(false);
  const canvasRef = React.useRef(null);

  useEffect(() => {
    let revoked = null;
    // Try to fetch the backend-rendered QR
    fetch(passAffiliates.qrImageUrl())
      .then((r) => (r.ok ? r.blob() : Promise.reject(r.status)))
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        revoked = url;
        setImgUrl(url);
      })
      .catch(() => {
        // Fallback to client-side QR generation
        if (canvasRef.current && affiliate?.slug) {
          const url = `${window.location.origin}/pass/${affiliate.slug}`;
          QRCode.toCanvas(canvasRef.current, url, { width: 200, margin: 2 }, () => setFallbackOk(true));
        }
      });
    return () => {
      if (revoked) URL.revokeObjectURL(revoked);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrToken, affiliate?.slug]);

  if (imgUrl) {
    return <img src={imgUrl} alt="QR del afiliado" className="w-48 h-48 border border-slate-200 rounded-lg bg-white" />;
  }
  return (
    <div className="w-48 h-48 border border-slate-200 rounded-lg bg-white flex items-center justify-center">
      <canvas ref={canvasRef} />
      {!fallbackOk && <Loader2 className="w-5 h-5 animate-spin text-slate-300 absolute" />}
    </div>
  );
}

function DownloadBtn({ label, size, template }) {
  const url = passAffiliates.printMaterialUrl({ size, template });
  return (
    <a
      href={url} target="_blank" rel="noreferrer"
      className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold"
    >
      <Download className="w-4 h-4" /> {label}
    </a>
  );
}

function CuratedCatalog() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    let alive = true;
    passAffiliates.availableOffers()
      .then((res) => alive && setOffers(res.data.offers || []))
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  async function toggleFeatured(offerId, currentValue) {
    setSaving(offerId);
    try {
      const newFeatured = offers.filter((o) => o.id !== offerId && o.is_featured).map((o) => o.id);
      if (!currentValue) newFeatured.push(offerId);
      const hidden = offers.filter((o) => o.is_hidden).map((o) => o.id);
      await passAffiliates.updateCatalog({ featured_offer_ids: newFeatured, hidden_offer_ids: hidden });
      setOffers((prev) => prev.map((o) => o.id === offerId ? { ...o, is_featured: !currentValue, is_hidden: false } : o));
      toast.success(currentValue ? 'Quitada de destacadas' : 'Marcada como destacada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(null);
    }
  }

  async function toggleHidden(offerId, currentValue) {
    setSaving(offerId);
    try {
      const newHidden = offers.filter((o) => o.id !== offerId && o.is_hidden).map((o) => o.id);
      if (!currentValue) newHidden.push(offerId);
      const featured = offers.filter((o) => o.is_featured).map((o) => o.id);
      await passAffiliates.updateCatalog({ featured_offer_ids: featured, hidden_offer_ids: newHidden });
      setOffers((prev) => prev.map((o) => o.id === offerId ? { ...o, is_hidden: !currentValue, is_featured: false } : o));
      toast.success(currentValue ? 'Mostrada de nuevo' : 'Oculta en tu catálogo');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(null);
    }
  }

  return (
    <Section title="Mi catálogo personalizado" icon={Sparkles}>
      <p className="text-sm text-slate-500 mb-4">
        Marca como <strong>destacadas</strong> las ofertas que más te gusten y <strong>oculta</strong> las que no quieres
        recomendar. Solo aparecerán las visibles en tu perfil <span className="font-mono text-xs">/pass/{'<tu slug>'}</span>.
      </p>
      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : offers.length === 0 ? (
        <p className="text-sm text-slate-500">Sin ofertas activas todavía.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {offers.map((o) => (
            <CatalogCard
              key={o.id}
              offer={o}
              onFeature={() => toggleFeatured(o.id, o.is_featured)}
              onHide={() => toggleHidden(o.id, o.is_hidden)}
              saving={saving === o.id}
            />
          ))}
        </div>
      )}
    </Section>
  );
}

function CuratedGuides() {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    let alive = true;
    passAffiliates.availableGuides()
      .then((res) => alive && setGuides(res.data.guides || []))
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  async function toggleFeatured(guideId, currentValue) {
    setSaving(guideId);
    try {
      const newFeatured = guides.filter((g) => g.id !== guideId && g.is_featured).map((g) => g.id);
      if (!currentValue) newFeatured.push(guideId);
      const hidden = guides.filter((g) => g.is_hidden).map((g) => g.id);
      await passAffiliates.updateCatalog({ featured_guide_ids: newFeatured, hidden_guide_ids: hidden });
      setGuides((prev) => prev.map((g) => g.id === guideId ? { ...g, is_featured: !currentValue, is_hidden: false } : g));
      toast.success(currentValue ? 'Quitada de destacadas' : 'Marcada como destacada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(null);
    }
  }

  async function toggleHidden(guideId, currentValue) {
    setSaving(guideId);
    try {
      const newHidden = guides.filter((g) => g.id !== guideId && g.is_hidden).map((g) => g.id);
      if (!currentValue) newHidden.push(guideId);
      const featured = guides.filter((g) => g.is_featured).map((g) => g.id);
      await passAffiliates.updateCatalog({ featured_guide_ids: featured, hidden_guide_ids: newHidden });
      setGuides((prev) => prev.map((g) => g.id === guideId ? { ...g, is_hidden: !currentValue, is_featured: false } : g));
      toast.success(currentValue ? 'Mostrada de nuevo' : 'Oculta en tu catálogo');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(null);
    }
  }

  return (
    <Section title="Mis guías recomendadas" icon={BookOpen}>
      <p className="text-sm text-slate-500 mb-4">
        Contenido <strong>gratuito</strong> (playas, miradores, museos) que aparece arriba de tus ofertas comerciales.
        No genera comisión, pero da valor a tus clientes y hace tu QR más útil.
      </p>
      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : guides.length === 0 ? (
        <p className="text-sm text-slate-500">Aún no hay guías publicadas por el equipo de Pass.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {guides.map((g) => (
            <GuideTogglerCard
              key={g.id}
              guide={g}
              onFeature={() => toggleFeatured(g.id, g.is_featured)}
              onHide={() => toggleHidden(g.id, g.is_hidden)}
              saving={saving === g.id}
            />
          ))}
        </div>
      )}
    </Section>
  );
}

function GuideTogglerCard({ guide, onFeature, onHide, saving }) {
  return (
    <div className={`border rounded-xl overflow-hidden flex flex-col ${guide.is_hidden ? 'opacity-50 border-slate-200' : 'border-slate-200'}`}>
      <div className="aspect-[16/9] bg-slate-100 relative">
        {guide.cover_image_url ? (
          <img src={guide.cover_image_url} alt={guide.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-teal-400">
            <BookOpen className="w-10 h-10" />
          </div>
        )}
        {guide.is_featured && (
          <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-orange-500 text-white text-[10px] font-bold inline-flex items-center gap-1">
            <Star className="w-3 h-3 fill-current" /> DESTACADA
          </span>
        )}
      </div>
      <div className="p-3 flex-1">
        <h3 className="text-sm font-semibold text-slate-900 leading-tight line-clamp-2">{guide.title}</h3>
        <p className="text-xs text-slate-500 mt-0.5">{guide.category} · {guide.city}</p>
      </div>
      <div className="flex border-t border-slate-200">
        <button
          type="button" onClick={onFeature} disabled={saving}
          className={`flex-1 text-xs font-semibold py-2 disabled:opacity-50 ${guide.is_featured ? 'bg-orange-50 text-orange-700' : 'text-slate-600 hover:bg-slate-50'}`}
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin inline" /> : <Star className="w-3 h-3 inline mr-1" />}
          {guide.is_featured ? 'Quitar destacada' : 'Destacar'}
        </button>
        <button
          type="button" onClick={onHide} disabled={saving}
          className={`flex-1 text-xs font-semibold py-2 border-l border-slate-200 disabled:opacity-50 ${guide.is_hidden ? 'bg-slate-100 text-slate-700' : 'text-slate-600 hover:bg-slate-50'}`}
        >
          {guide.is_hidden ? <Eye className="w-3 h-3 inline mr-1" /> : <EyeOff className="w-3 h-3 inline mr-1" />}
          {guide.is_hidden ? 'Mostrar' : 'Ocultar'}
        </button>
      </div>
    </div>
  );
}

function CatalogCard({ offer, onFeature, onHide, saving }) {
  const benefit = offer.customer_benefits?.[0]?.name;
  return (
    <div className={`border rounded-xl overflow-hidden flex flex-col ${offer.is_hidden ? 'opacity-50 border-slate-200' : 'border-slate-200'}`}>
      <div className="aspect-[4/3] bg-slate-100 relative">
        {offer.images?.[0] ? (
          <img src={offer.images[0]} alt={offer.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300">
            <ImageIcon className="w-10 h-10" />
          </div>
        )}
        {offer.is_featured && (
          <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-orange-500 text-white text-[10px] font-bold inline-flex items-center gap-1">
            <Star className="w-3 h-3 fill-current" /> DESTACADA
          </span>
        )}
      </div>
      <div className="p-3 flex-1 flex flex-col">
        <h3 className="text-sm font-semibold text-slate-900 leading-tight line-clamp-2">{offer.title}</h3>
        <p className="text-xs text-slate-500 mt-0.5">{offer.business_name || offer.category}</p>
        {benefit && <p className="text-xs text-teal-600 font-medium mt-1.5 line-clamp-1">{benefit}</p>}
      </div>
      <div className="flex border-t border-slate-200">
        <button
          type="button"
          onClick={onFeature}
          disabled={saving}
          className={`flex-1 text-xs font-semibold py-2 disabled:opacity-50 ${offer.is_featured ? 'bg-orange-50 text-orange-700' : 'text-slate-600 hover:bg-slate-50'}`}
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin inline" /> : <Star className="w-3 h-3 inline mr-1" />}
          {offer.is_featured ? 'Quitar destacada' : 'Destacar'}
        </button>
        <button
          type="button"
          onClick={onHide}
          disabled={saving}
          className={`flex-1 text-xs font-semibold py-2 border-l border-slate-200 disabled:opacity-50 ${offer.is_hidden ? 'bg-slate-100 text-slate-700' : 'text-slate-600 hover:bg-slate-50'}`}
        >
          {offer.is_hidden ? <Eye className="w-3 h-3 inline mr-1" /> : <EyeOff className="w-3 h-3 inline mr-1" />}
          {offer.is_hidden ? 'Mostrar' : 'Ocultar'}
        </button>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children, accent }) {
  const iconColor = accent === 'orange' ? 'text-orange-500' : 'text-teal-600';
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
      <h2 className="text-lg font-semibold text-slate-900 mb-4 inline-flex items-center gap-2">
        <Icon className={`w-5 h-5 ${iconColor}`} /> {title}
      </h2>
      {children}
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-900 text-right">{value}</span>
    </div>
  );
}

function Stat({ label, value, accent }) {
  const color = accent === 'orange' ? 'text-orange-600' : 'text-slate-900';
  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-xl font-bold ${color}`}>${Number(value || 0).toFixed(2)}</p>
    </div>
  );
}

function PayoutStatusBadge({ status }) {
  const map = {
    pending:  { label: 'Pendiente', icon: Clock, cls: 'bg-amber-50 text-amber-700' },
    approved: { label: 'Aprobado',  icon: CheckCircle2, cls: 'bg-blue-50 text-blue-700' },
    paid:     { label: 'Pagado',    icon: CheckCircle2, cls: 'bg-green-50 text-green-700' },
    rejected: { label: 'Rechazado', icon: Clock, cls: 'bg-red-50 text-red-700' },
  };
  const m = map[status] || { label: status, icon: Clock, cls: 'bg-slate-100 text-slate-700' };
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${m.cls}`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}
